<template>
  <div class="image-compress">
    <h1>图片压缩工具</h1>
    
    <div class="compress-container">
      <!-- 上传区域 -->
      <div class="upload-section">
        <el-upload
          class="upload-area"
          drag
          :auto-upload="false"
          :show-file-list="false"
          accept="image/*"
          :on-change="handleFileChange"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">
            拖拽图片到此处或 <em>点击上传</em>
          </div>
          <template #tip>
            <div class="el-upload__tip">
              支持 jpg、png、webp 等常见图片格式
            </div>
          </template>
        </el-upload>
      </div>

      <!-- 压缩选项 -->
      <div class="options-section" v-if="imageInfo.file">
        <h3>压缩选项</h3>
        <div class="option-item">
          <span>质量：{{ quality }}%</span>
          <el-slider
            v-model="quality"
            :min="1"
            :max="100"
            @change="handleCompress"
          />
        </div>
        <div class="option-item">
          <span>最大宽度：{{ maxWidth }}px</span>
          <el-slider
            v-model="maxWidth"
            :min="100"
            :max="4000"
            :step="100"
            @change="handleCompress"
          />
        </div>
        <div class="option-item">
          <span>输出格式：</span>
          <el-radio-group v-model="outputFormat" @change="handleCompress">
            <el-radio-button label="jpeg">JPEG</el-radio-button>
            <el-radio-button label="png">PNG</el-radio-button>
            <el-radio-button label="webp">WebP</el-radio-button>
          </el-radio-group>
        </div>
      </div>

      <!-- 预览区域 -->
      <div class="preview-section" v-if="imageInfo.file">
        <div class="preview-container">
          <!-- 原图预览 -->
          <div class="preview-item">
            <h3>原图</h3>
            <div class="image-wrapper">
              <img :src="imageInfo.originalUrl" alt="原图" />
            </div>
            <div class="image-info">
              <p>尺寸：{{ imageInfo.originalWidth }} x {{ imageInfo.originalHeight }}</p>
              <p>大小：{{ formatFileSize(imageInfo.originalSize) }}</p>
            </div>
          </div>

          <!-- 压缩后预览 -->
          <div class="preview-item" v-if="compressedImage.url">
            <h3>压缩后</h3>
            <div class="image-wrapper">
              <img :src="compressedImage.url" alt="压缩后" />
            </div>
            <div class="image-info">
              <p>尺寸：{{ compressedImage.width }} x {{ compressedImage.height }}</p>
              <p>大小：{{ formatFileSize(compressedImage.size) }}</p>
              <p>压缩率：{{ compressionRatio }}%</p>
            </div>
          </div>
        </div>

        <!-- 下载按钮 -->
        <div class="button-group" v-if="compressedImage.url">
          <el-button type="primary" @click="downloadImage">下载压缩后的图片</el-button>
        </div>
      </div>

      <!-- 说明区域 -->
      <div class="info-section">
        <h3>使用说明</h3>
        <ul>
          <li>支持拖拽或点击上传图片</li>
          <li>可调节压缩质量和最大宽度</li>
          <li>支持 JPEG、PNG、WebP 多种输出格式</li>
          <li>保持原始宽高比进行压缩</li>
          <li>建议图片大小不超过 10MB</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { UploadFilled } from '@element-plus/icons-vue'

const quality = ref(80)
const maxWidth = ref(1920)
const outputFormat = ref('jpeg')

const imageInfo = ref({
  file: null,
  originalUrl: '',
  originalSize: 0,
  originalWidth: 0,
  originalHeight: 0
})

const compressedImage = ref({
  url: '',
  size: 0,
  width: 0,
  height: 0
})

// 计算压缩率
const compressionRatio = computed(() => {
  if (!imageInfo.value.originalSize || !compressedImage.value.size) return 0
  const ratio = ((imageInfo.value.originalSize - compressedImage.value.size) / imageInfo.value.originalSize) * 100
  return Math.round(ratio)
})

// 处理文件选择
const handleFileChange = (file) => {
  if (!file || !file.raw) {
    ElMessage.error('文件上传失败')
    return
  }

  const uploadFile = file.raw

  // 检查文件大小
  if (uploadFile.size > 10 * 1024 * 1024) {
    ElMessage.error('图片大小不能超过10MB')
    return
  }

  // 检查文件类型
  if (!uploadFile.type || !uploadFile.type.startsWith('image/')) {
    ElMessage.error('请上传图片文件')
    return
  }

  imageInfo.value.file = uploadFile
  imageInfo.value.originalSize = uploadFile.size
  
  // 读取图片信息
  const reader = new FileReader()
  reader.onload = (e) => {
    const img = new Image()
    img.onload = () => {
      imageInfo.value.originalWidth = img.width
      imageInfo.value.originalHeight = img.height
      imageInfo.value.originalUrl = e.target.result
      handleCompress()
    }
    img.src = e.target.result
  }
  reader.readAsDataURL(uploadFile)
}

// 处理压缩
const handleCompress = () => {
  if (!imageInfo.value.file) return

  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const img = new Image()

  img.onload = () => {
    // 计算新的尺寸
    let newWidth = img.width
    let newHeight = img.height

    if (newWidth > maxWidth.value) {
      const ratio = maxWidth.value / newWidth
      newWidth = maxWidth.value
      newHeight = Math.round(newHeight * ratio)
    }

    // 设置画布尺寸
    canvas.width = newWidth
    canvas.height = newHeight

    // 绘制图片
    ctx.drawImage(img, 0, 0, newWidth, newHeight)

    // 导出压缩后的图片
    const compressedUrl = canvas.toDataURL(`image/${outputFormat.value}`, quality.value / 100)
    
    // 计算压缩后的大小
    const base64Size = compressedUrl.length * 0.75
    
    compressedImage.value = {
      url: compressedUrl,
      size: base64Size,
      width: newWidth,
      height: newHeight
    }
  }

  img.src = imageInfo.value.originalUrl
}

// 下载压缩后的图片
const downloadImage = () => {
  if (!compressedImage.value.url) return

  const link = document.createElement('a')
  link.download = `compressed_image.${outputFormat.value}`
  link.href = compressedImage.value.url
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

// 格式化文件大小
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return (bytes / Math.pow(k, i)).toFixed(2) + ' ' + sizes[i]
}
</script>

<style scoped>
.image-compress {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  margin-top: 80px;
}

.compress-container {
  margin-top: 20px;
}

.upload-section {
  margin: 20px 0;
}

.upload-area {
  width: 100%;
}

.options-section {
  margin: 20px 0;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.option-item {
  margin: 15px 0;
}

.preview-section {
  margin: 20px 0;
}

.preview-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 20px 0;
}

.preview-item {
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  padding: 15px;
}

.image-wrapper {
  width: 100%;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.image-wrapper img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.image-info {
  margin-top: 10px;
  color: #606266;
}

.button-group {
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.info-section {
  margin-top: 30px;
  padding: 20px;
  background-color: #f5f7fa;
  border-radius: 8px;
}

.info-section ul {
  padding-left: 20px;
  margin: 10px 0;
}

.info-section li {
  margin: 8px 0;
  line-height: 1.5;
}

@media (max-width: 768px) {
  .preview-container {
    grid-template-columns: 1fr;
  }
}
</style> 